import {
  Alert,
  Button,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Modal,
  ModalClose,
  ModalDialog,
  Option,
  Select,
  Stack,
  Textarea,
  Typography,
} from '@mui/joy';
import { useState } from 'react';
import { adminResetMfa } from '../api/mfaAdmin.api';
import { METHOD_LABELS } from '../labels/mfaAdmin.labels';
import type { MfaVerificationMethod } from '../types/mfaAdmin.types';

type Props = {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  targetUserId: string;
  targetUserEmail: string;
};

// Dedicated reset modal — kept separate from the general UserModal because
// the safety envelope is different: reset clears every second-factor the
// user has (TOTP, recovery codes, admin email code) and the audit trail
// depends on the notes being real. UI enforces the verification-method
// pick and a non-empty notes field before enabling the confirm button.

const AdminResetModal = (props: Props) => {
  const { isOpen, onClose, onSuccess, targetUserId, targetUserEmail } = props;
  const [method, setMethod] = useState<MfaVerificationMethod | ''>('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const reset = () => {
    setMethod('');
    setNotes('');
    setSubmitting(false);
    setError(null);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!method || notes.trim().length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      await adminResetMfa(targetUserId, {
        verificationMethod: method,
        verificationNotes: notes.trim(),
      });
      reset();
      onSuccess();
    } catch (err: any) {
      setError(
        err?.response?.data?.message ??
          'Reset mislukt. Controleer de verbinding en probeer opnieuw.'
      );
      setSubmitting(false);
    }
  };

  const canSubmit = Boolean(method) && notes.trim().length > 0 && !submitting;

  return (
    <Modal open={isOpen} onClose={handleClose}>
      <ModalDialog size="md" sx={{ minWidth: 480 }}>
        <ModalClose />
        <DialogTitle>Tweestapsverificatie resetten</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ marginTop: 2 }}>
            <Alert color="warning" variant="soft">
              <Typography level="body-sm">
                Reset verwijdert de tweestapsverificatie van deze gebruiker
                volledig. Zij kunnen daarna zonder tweede stap inloggen tot ze
                zelf opnieuw activeren.
              </Typography>
            </Alert>

            <Typography level="body-sm">
              <strong>Gebruiker:</strong> {targetUserEmail}
            </Typography>

            <FormControl>
              <FormLabel>Verificatiemethode</FormLabel>
              <Select
                value={method}
                onChange={(_, value) =>
                  setMethod((value as MfaVerificationMethod) ?? '')
                }
                placeholder="Kies hoe je de identiteit hebt bevestigd"
              >
                {(Object.keys(METHOD_LABELS) as MfaVerificationMethod[]).map(
                  (m) => (
                    <Option key={m} value={m}>
                      {METHOD_LABELS[m]}
                    </Option>
                  )
                )}
              </Select>
            </FormControl>

            <FormControl>
              <FormLabel>Notities (verplicht, minimaal 1 teken)</FormLabel>
              <Textarea
                minRows={3}
                maxRows={6}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Bijv. gebeld op 06-1234567 op 2026-07-07, geboortedatum bevestigd."
              />
            </FormControl>

            {error && (
              <Alert color="danger" variant="soft">
                {error}
              </Alert>
            )}

            <Stack direction="row" spacing={1}>
              <Button
                fullWidth
                color="danger"
                onClick={handleSubmit}
                loading={submitting}
                disabled={!canSubmit}
              >
                Resetten
              </Button>
              <Button
                fullWidth
                color="neutral"
                variant="soft"
                onClick={handleClose}
                disabled={submitting}
              >
                Annuleren
              </Button>
            </Stack>
          </Stack>
        </DialogContent>
      </ModalDialog>
    </Modal>
  );
};

export default AdminResetModal;
